import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription } from '../components/ui/alert';
import { 
  Settings, CheckCircle, Clock, AlertCircle, ExternalLink,
  RefreshCw, Server, Database, Mail, Shield, Brain, Link2
} from 'lucide-react';
import { api } from '../api/client';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';

const IntegrationStatusPage = () => {
  const [loading, setLoading] = useState(true);
  const [readiness, setReadiness] = useState(null);

  useEffect(() => {
    fetchReadiness();
  }, []);

  const fetchReadiness = async () => {
    try {
      setLoading(true);
      const response = await api.get('/system/integrations/status');
      setReadiness(response.data);
    } catch (error) {
      console.error('Error fetching readiness:', error);
      toast.error('Failed to load integration status');
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'ready':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'pending':
        return <Clock className="w-5 h-5 text-amber-500" />;
      case 'error':
        return <AlertCircle className="w-5 h-5 text-red-500" />;
      default:
        return <Clock className="w-5 h-5 text-gray-400" />;
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'ready':
        return <Badge className="bg-green-100 text-green-800">Ready</Badge>;
      case 'pending':
        return <Badge className="bg-amber-100 text-amber-800">Pending</Badge>;
      case 'error':
        return <Badge variant="destructive">Error</Badge>;
      default:
        return <Badge variant="secondary">Unknown</Badge>;
    }
  };

  const getServiceIcon = (name) => {
    switch (name) {
      case 'ado':
        return <Server className="w-5 h-5" />;
      case 'jira':
        return <Database className="w-5 h-5" />;
      case 'sendgrid':
        return <Mail className="w-5 h-5" />;
      case 'hcaptcha':
        return <Shield className="w-5 h-5" />;
      case 'openai':
        return <Brain className="w-5 h-5" />;
      default:
        return <Link2 className="w-5 h-5" />;
    }
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <RefreshCw className="w-8 h-8 animate-spin text-[var(--clr-brand-primary)]" />
      </div>
    );
  }

  const integrations = readiness?.integrations || [];
  const summary = readiness?.summary || {};

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[var(--clr-text-primary)] flex items-center gap-2">
            <Settings className="w-7 h-7 text-[var(--clr-brand-primary)]" />
            Integration Status
          </h1>
          <p className="text-[var(--clr-text-secondary)] mt-1">
            External service connections and readiness
          </p>
        </div>
        <Button variant="outline" onClick={fetchReadiness}>
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh Status
        </Button>
      </div>

      {/* Summary Banner */}
      {summary.pending > 0 && (
        <Alert className="border-amber-200 bg-amber-50">
          <AlertCircle className="w-4 h-4 text-amber-600" />
          <AlertDescription className="text-amber-800">
            {summary.pending} of {summary.total} integrations are pending configuration. 
            Some features may be limited until credentials are provided.
          </AlertDescription>
        </Alert>
      )}

      {/* Summary Cards */}
      <div className="grid md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-[var(--clr-text-secondary)]">Ready</p>
                <p className="text-3xl font-bold text-green-600">{summary.ready || 0}</p>
              </div>
              <CheckCircle className="w-10 h-10 text-green-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-[var(--clr-text-secondary)]">Pending</p>
                <p className="text-3xl font-bold text-amber-600">{summary.pending || 0}</p>
              </div>
              <Clock className="w-10 h-10 text-amber-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-[var(--clr-text-secondary)]">Total</p>
                <p className="text-3xl font-bold">{summary.total || 0}</p>
              </div>
              <Settings className="w-10 h-10 text-[var(--clr-text-secondary)]" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Integration List */}
      <Card>
        <CardHeader>
          <CardTitle>External Services</CardTitle>
          <CardDescription>Connection status for all integrated services</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {integrations.map((integration) => (
              <div
                key={integration.name}
                className="flex items-center justify-between p-4 border rounded-lg hover:bg-[var(--clr-surface-subtle)] transition-colors"
                data-testid={`integration-${integration.name}`}
              >
                <div className="flex items-center gap-4">
                  <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                    integration.status === 'ready' 
                      ? 'bg-green-100' 
                      : integration.status === 'pending' 
                        ? 'bg-amber-100' 
                        : 'bg-red-100'
                  }`}>
                    {getServiceIcon(integration.name)}
                  </div>
                  <div>
                    <p className="font-medium text-[var(--clr-text-primary)]">
                      {integration.display_name}
                    </p>
                    <p className="text-sm text-[var(--clr-text-secondary)]">
                      {integration.configured 
                        ? 'Configured and connected' 
                        : 'Credentials not provided'}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-4">
                  {getStatusBadge(integration.status)}
                  
                  {integration.settings_link && (
                    <Button variant="ghost" size="sm" asChild>
                      <Link to={integration.settings_link}>
                        <ExternalLink className="w-4 h-4" />
                      </Link>
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Help Section */}
      <Card>
        <CardHeader>
          <CardTitle>Need Help?</CardTitle>
          <CardDescription>Resources for configuring integrations</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="p-4 border rounded-lg">
              <h4 className="font-medium mb-2">Azure DevOps</h4>
              <p className="text-sm text-[var(--clr-text-secondary)] mb-3">
                Connect your ADO organization for work item sync
              </p>
              <Button variant="outline" size="sm" asChild>
                <Link to="/settings/integrations/azure-devops">
                  Configure ADO
                </Link>
              </Button>
            </div>
            
            <div className="p-4 border rounded-lg">
              <h4 className="font-medium mb-2">Jira</h4>
              <p className="text-sm text-[var(--clr-text-secondary)] mb-3">
                Connect Jira Cloud for issue tracking
              </p>
              <Button variant="outline" size="sm" asChild>
                <Link to="/settings/jira">
                  Configure Jira
                </Link>
              </Button>
            </div>
            
            <div className="p-4 border rounded-lg">
              <h4 className="font-medium mb-2">Email (SendGrid)</h4>
              <p className="text-sm text-[var(--clr-text-secondary)] mb-3">
                Enable email notifications with SendGrid
              </p>
              <Button variant="outline" size="sm" asChild>
                <Link to="/admin/integrations">
                  Configure Email
                </Link>
              </Button>
            </div>
            
            <div className="p-4 border rounded-lg">
              <h4 className="font-medium mb-2">AI Features</h4>
              <p className="text-sm text-[var(--clr-text-secondary)] mb-3">
                Enable AI-powered features with OpenAI
              </p>
              <Button variant="outline" size="sm" asChild>
                <Link to="/admin/integrations">
                  Configure AI
                </Link>
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default IntegrationStatusPage;
